<script lang="ts" setup>
  import { documentToHtmlString, type Options } from '@contentful/rich-text-html-renderer'
  import { type Document, BLOCKS, INLINES } from '@contentful/rich-text-types'
  import { sanitizeRichBodyText } from '~/utils/string'

  const props = withDefaults(
    defineProps<{
      text: Document
      darkMode?: boolean
    }>(),
    {
      darkMode: false
    }
  )

  const options: Partial<Options> = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, next) => {
        // If the paragraph is empty, return a <br/> tag instead of an empty <p> tag
        // to avoid the browser collapsing the empty <p> tag
        if (
          node.nodeType === 'paragraph' &&
          node.content.length === 1 &&
          node.content[0].nodeType === 'text' &&
          node.content[0].value === ''
        ) {
          return '<br/>'
        }

        return `<p>${next(node.content)}</p>`
      },
      [INLINES.HYPERLINK]: (node, next) => {
        return `<a class="simple-text-link" href="${node.data.uri}">${next(node.content)}</a>`
      },
      [BLOCKS.TABLE]: (node, next) => {
        return `<div class="rich-text-table-wrapper"><table class="rich-text-table">${next(node.content)}</table></div>`
      },
      [BLOCKS.TABLE_ROW]: (node, next) => {
        return `<tr>${next(node.content)}</tr>`
      },
      [BLOCKS.TABLE_HEADER_CELL]: (node, next) => {
        return `<th>${next(node.content)}</th>`
      },
      [BLOCKS.TABLE_CELL]: (node, next) => {
        return `<td>${next(node.content)}</td>`
      }
    }
  }

  const textContent = computed<string>(() => {
    return documentToHtmlString(props.text as Document, options)
  })
</script>

<template>
  <div
    v-if="textContent"
    :class="['c-body-rich-text', 'body', { 'dark-mode': darkMode }]"
    v-html="sanitizeRichBodyText(textContent)"
  />
</template>

<style lang="scss" scoped>
  .c-body-rich-text {
    :deep(.rich-text-table-wrapper) {
      overflow-x: auto;
      margin: 1.5rem 0;
      border-radius: 4px;
      -webkit-overflow-scrolling: touch;

      @media (min-width: 768px) {
        overflow-x: visible;
      }
    }

    :deep(.rich-text-table) {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.875rem;
      line-height: 1.5;

      @media (max-width: 767px) {
        min-width: 600px;
      }

      th,
      td {
        padding: 0.75rem;
        text-align: left;
        border: 1px solid var(--color-text-body);

        @media (max-width: 767px) {
          padding: 0.625rem;
          font-size: 0.8125rem;
        }
      }

      th {
        background-color: var(--color-text-body);
        font-weight: 600;
        color: var(--color-text-inverse);

        @media (max-width: 767px) {
          white-space: nowrap;
        }
      }

      td {
        background-color: var(--color-background-primary);
      }

      tbody tr:nth-child(even) {
        background-color: rgba(0, 0, 0, 0.02);
      }

      tbody tr:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }
    }
  }

  .c-body-rich-text.dark-mode {
    :deep(.rich-text-table) {
      th,
      td {
        border: 1px solid #ffffff;
      }

      th {
        background-color: #ffffff;
        color: var(--color-text-body);

        @media (max-width: 767px) {
          white-space: nowrap;
        }
      }

      td {
        background-color: transparent;
      }

      tbody tr:nth-child(even) {
        background-color: rgba(255, 255, 255, 0.02);
      }

      tbody tr:hover {
        background-color: rgba(255, 255, 255, 0.04);
      }
    }
  }
</style>
